import type {
  AudienceAddressField,
  AudienceMember,
  AudienceStatus,
  Campaign,
  CampaignAudienceFilters,
  CampaignBookingFilters,
  CampaignChannel,
  CampaignRecipient,
  CampaignResendSegment,
  CampaignSendMode,
  CampaignStats,
  MergeField,
  SendingOptions,
  SmsSendingOptions,
} from '../../api/types';
import { listTime } from '../../lib/commsFormat';
import { CHANNEL_META } from '../../lib/commsMeta';
import { type DateFormats, formatDate, formatTime } from '../../lib/dateFormat';
import { formatTenantMoney } from '../../lib/tenantCurrency';
import { dayIndex, instantFromWall } from '../../lib/timeZone';

/** Pure campaign helpers shared by the list/builder/results screens — tested. */

export const AUDIENCE_LABELS: Record<Campaign['audienceType'], string> = {
  customers: 'Customers',
  affiliates: 'Affiliates',
  drivers: 'Chauffeurs / drivers',
  custom: 'Custom list',
};

/**
 * `zone` is the tenant's dispatch timezone and `formats` its display patterns —
 * see lib/tenantTime. Both come from `useTimeFormat()` at the call site.
 */
export function campaignWhen(
  campaign: Campaign,
  zone?: string | null,
  formats?: DateFormats,
  now: Date = new Date(),
): string {
  /**
   * A scheduled campaign is the one row on this screen whose date is in the
   * FUTURE, so it cannot go through `listTime` — that reads a day difference the
   * other way round and would print a campaign due next month as a bare "09:00".
   * Near days are named because "Tomorrow 09:00" is the check somebody actually
   * wants; everything further out gets the tenant's own date pattern.
   */
  if (campaign.status === 'scheduled') {
    const at = campaign.scheduledAt ? new Date(campaign.scheduledAt) : null;
    if (!at || Number.isNaN(at.getTime())) return 'Scheduled';
    const clock = formatTime(at, zone, formats?.timeFormat);
    const days = dayIndex(at, zone) - dayIndex(now, zone);
    if (days === 0) return `Today ${clock}`;
    if (days === 1) return `Tomorrow ${clock}`;
    return `${formatDate(at, zone, formats?.dateFormat)} ${clock}`;
  }
  // A stopped campaign reads as how far it got, the same as a running one: the
  // question on the list screen is what it did to the list, and "how much of it
  // went out" is that answer whether or not it is still moving.
  if (campaign.status === 'sending' || campaign.status === 'stopped') {
    const { total, sent, failed, bounced } = campaign.stats ?? {
      total: 0,
      sent: 0,
      failed: 0,
      bounced: 0,
    };
    const done = sent + failed + bounced;
    const share = total > 0 ? Math.round((done / total) * 100) : 0;
    return campaign.status === 'stopped' ? `Stopped · ${share}%` : `Running · ${share}%`;
  }
  if (campaign.status === 'draft') return 'Not sent';
  // A campaign that failed BEFORE it started is a scheduled send that never left
  // — dating it by `createdAt` would put the day somebody drafted it under a red
  // Failed chip, which reads as "it went out then and broke".
  if (campaign.status === 'failed' && !campaign.startedAt) return 'Did not send';
  const at = campaign.completedAt ?? campaign.startedAt ?? campaign.createdAt;
  return listTime(at, now, zone, formats);
}

/**
 * The server's own schedule bounds, mirrored here so the picker can explain
 * itself before a round trip. They are not a second opinion — the server refuses
 * the same two things, and this exists so the operator learns it from the form
 * rather than from a red box after pressing the button.
 */
export const SCHEDULE_MIN_LEAD_MS = 60_000;
export const SCHEDULE_MAX_HORIZON_MS = 365 * 24 * 3600 * 1000;

/**
 * Why this wall-clock choice cannot be scheduled, or null when it can.
 *
 * `wall` is what the `datetime-local` control holds — `YYYY-MM-DDTHH:mm` with no
 * zone on it — and it is read as a time in the TENANT's zone, never the browser's
 * (see `instantFromWall`). That is the whole reason this is a helper and not two
 * lines in the builder: an operator in Lisbon scheduling their New York company's
 * campaign for 09:00 means nine o'clock in New York, and getting it wrong is
 * silent until the mail arrives at the wrong end of the working day.
 */
export function scheduleBlocker(
  wall: string,
  zone: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!wall.trim()) return 'Pick a date and time';
  const at = instantFromWall(wall, zone);
  if (!at) return 'Pick a date and time';
  const lead = at.getTime() - now.getTime();
  if (lead < SCHEDULE_MIN_LEAD_MS) return 'Pick a time at least a minute from now';
  if (lead > SCHEDULE_MAX_HORIZON_MS) return 'A campaign can be scheduled up to a year ahead';
  return null;
}

/**
 * Who a resend can go to, and how many that is.
 *
 * The sizes come out of the campaign's own `stats` rather than a second request,
 * because the server resolves each segment with predicates written to produce
 * exactly these numbers (controller/campaignCont.js, RESEND_SEGMENTS). That
 * agreement is the load-bearing part — an operator picks "bounces · 26" and has
 * to get 26 — so `scripts/verify/campaignresend.js` asserts the two sides match.
 */
type ResendChoice = {
  key: Exclude<CampaignResendSegment, 'custom'>;
  label: string;
  hint: string;
};

/**
 * The email funnel: sent, opened, bounced. `opened` is the signal here because
 * it is the only one a mailbox gives you — there is no delivery receipt.
 */
export const RESEND_SEGMENTS: ResendChoice[] = [
  { key: 'all', label: 'Everyone', hint: 'Every address this campaign resolved to' },
  { key: 'unopened', label: 'Did not open', hint: 'Reached them, but never opened' },
  { key: 'opened', label: 'Opened it', hint: 'A follow-up to the people who read it' },
  // Offered only once a campaign tracked clicks — sized 0 otherwise, which the
  // dialog renders as unavailable rather than as "nobody clicked".
  { key: 'clicked', label: 'Clicked a link', hint: 'The people who showed real interest' },
  { key: 'unclicked', label: 'Opened, did not click', hint: 'Read it, but nothing caught them' },
  { key: 'bounced', label: 'Bounced', hint: 'Came back undelivered' },
  { key: 'failed', label: 'Failed to send', hint: 'Never left the mailbox' },
];

/**
 * The text funnel, and it is a different one rather than a relabelling.
 *
 * SMS has no open — no pixel, no equivalent — so the openers row simply is not
 * offered. What it has instead is a real carrier receipt, which email never
 * gives you, and `undelivered` is the most useful group on the whole screen: a
 * handset that was switched off all afternoon is not a bad number, it is
 * somebody to text again this evening.
 */
export const SMS_RESEND_SEGMENTS: ResendChoice[] = [
  { key: 'all', label: 'Everyone', hint: 'Every number this campaign resolved to' },
  { key: 'undelivered', label: 'Not confirmed', hint: 'Sent, but the carrier never confirmed it' },
  { key: 'delivered', label: 'Delivered', hint: 'A follow-up to the people it reached' },
  // A tap on a shortened link — offered only once the campaign shortened them,
  // sized 0 otherwise like the email clickers.
  { key: 'clicked', label: 'Tapped a link', hint: 'The people who showed real interest' },
  { key: 'bounced', label: 'Bad number', hint: 'A landline, or a handset that does not exist' },
  { key: 'failed', label: 'Failed to send', hint: 'Never left the number' },
];

export const resendSegmentsFor = (channel: CampaignChannel): ResendChoice[] =>
  channel === 'sms' ? SMS_RESEND_SEGMENTS : RESEND_SEGMENTS;

const EMPTY_STATS: CampaignStats = {
  total: 0,
  pending: 0,
  sent: 0,
  failed: 0,
  bounced: 0,
  opened: 0,
  delivered: 0,
  suppressed: 0,
  unsubscribed: 0,
  capped: 0,
  clicked: 0,
};

export function resendSegmentSize(stats: CampaignStats | undefined, key: string): number {
  const s = { ...EMPTY_STATS, ...(stats ?? {}) };
  if (key === 'opened') return s.opened;
  if (key === 'unopened') return Math.max(0, s.sent - s.opened);
  // A click stamps an open too (server-side), so clickers are a subset of openers.
  if (key === 'clicked') return s.clicked ?? 0;
  if (key === 'unclicked') return Math.max(0, s.opened - (s.clicked ?? 0));
  if (key === 'delivered') return s.delivered;
  if (key === 'undelivered') return Math.max(0, s.sent - s.delivered);
  if (key === 'bounced') return s.bounced;
  if (key === 'failed') return s.failed;
  // `all` excludes the people who opted out, matching the server's predicate:
  // they are on the campaign but they are not somebody it may reach again. Nor
  // the ones this campaign left out because they had bounced before.
  return Math.max(0, s.total - s.suppressed - (s.bouncedBefore ?? 0));
}

/**
 * How a recipient is addressed, whichever channel this campaign is on. One
 * helper rather than `row.email ?? row.phoneNumber` at a dozen call sites, each
 * of which would be a place for a text campaign's table to render blanks.
 */
export const recipientAddress = (row: Pick<CampaignRecipient, 'email' | 'phoneNumber'>): string =>
  row.phoneNumber ?? row.email ?? '';

export const memberAddress = (row: Pick<AudienceMember, 'email' | 'phoneNumber'>): string =>
  row.phoneNumber ?? row.email ?? '';

/**
 * How a campaign's channel is labelled and coloured, in ONE place.
 *
 * The list and the results page both say it, and they have to agree — a screen
 * where the same campaign is "SMS" in one place and "Text" in another is a
 * screen an operator reads twice. Colours come from the module's own channel
 * palette (`CHANNEL_META`) rather than being picked here, so a campaign chip
 * matches the channel tab it belongs to.
 */
export const campaignChannelChip = (
  channel: CampaignChannel,
): { label: string; bg: string; fg: string } =>
  channel === 'sms'
    ? { label: 'SMS', bg: CHANNEL_META.sms.muted, fg: CHANNEL_META.sms.color }
    : { label: 'Email', bg: CHANNEL_META.email.muted, fg: CHANNEL_META.email.color };

/** Whether these sending options came back for a phone number rather than a mailbox. */
export const isSmsOptions = (
  options: SendingOptions | SmsSendingOptions | null | undefined,
): options is SmsSendingOptions => !!options && 'throughputPerMinute' in options;

/**
 * How long a text campaign will take, and why it might be slower than that.
 *
 * The pace is not a setting the operator chose — it is whatever is left of the
 * number after the agents using it are accounted for, and it MOVES while the
 * campaign runs. Saying so here is the difference between an operator reading a
 * slow send as the system working and reading it as the send being stuck.
 */
export function smsPaceSummary(options: SmsSendingOptions): string {
  if (!options.audienceSize) return 'Nothing to send yet';
  const minutes = options.estimatedMinutes;
  const span =
    minutes < 2
      ? 'about a minute'
      : minutes < 90
        ? `about ${minutes} minutes`
        : `about ${Math.round(minutes / 60)} hours`;
  if (options.busy) return `${span} once the line frees up — agents are using it right now`;
  return `${span} at ${options.currentAllowance}/min`;
}

/** The sentence under the pace: what is being held back, and for whom. */
export function smsReserveNote(options: SmsSendingOptions): string {
  const share = options.bulkSharePercent;
  const held = options.reservedPerMinute;
  const base =
    `This number carries about ${options.throughputPerMinute} segments a minute` +
    (options.throughputConfigured ? '' : ' by default') +
    `, and a campaign may take up to ${share}% of it.`;
  if (held == null) return base;
  return `${base} ${held}/min is being kept free for agent replies right now, so the campaign speeds up and slows down with them.`;
}

/**
 * Which choice the resend dialog opens on.
 *
 * Two rules, and the second is the one that matters. A group nobody is in cannot
 * be pressed, so opening on one gives the operator a greyed-out selection under a
 * button reading "Create draft for 0" — which reads as broken. And the order this
 * falls back through is deliberately NOT the order the choices are listed in:
 * `all` is last, because it is the only one that re-mails people who already got
 * the campaign — hard bounces included — and that is never a thing to pick on
 * somebody's behalf. It is what is left when nobody else is there.
 */
export function defaultResendSegment(
  stats: CampaignStats | undefined,
  pickedCount: number,
  channel: CampaignChannel = 'email',
): CampaignResendSegment {
  if (pickedCount > 0) return 'custom';
  const order: CampaignResendSegment[] =
    channel === 'sms'
      ? ['undelivered', 'failed', 'bounced', 'delivered', 'all']
      : ['unopened', 'failed', 'bounced', 'opened', 'all'];
  return order.find((key) => resendSegmentSize(stats, key) > 0) ?? 'all';
}

export const PRIORITY_LABELS: Record<number, string> = { 1: 'Normal', 2: 'VIP', 3: 'VVIP' };

export const ADDRESS_FIELDS: { field: AudienceAddressField; label: string }[] = [
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
  { field: 'country', label: 'Country' },
  { field: 'postalCode', label: 'Postal code' },
];

/**
 * The builder's per-audience targeting state. 'all' sends to everyone matching
 * the filters minus unticked people; 'manual' sends only to ticked people
 * (picked from the same filtered pool).
 */
/**
 * Booking-history inputs as the builder holds them — strings, so a box can be
 * empty while somebody is typing. Empty means "not filtered on".
 */
export interface BookingFilterState {
  lastTripWithinDays: string;
  noTripForDays: string;
  minTrips: string;
  maxTrips: string;
  minSpend: string;
  upcoming: '' | 'only' | 'exclude';
}

export const EMPTY_BOOKINGS: BookingFilterState = {
  lastTripWithinDays: '',
  noTripForDays: '',
  minTrips: '',
  maxTrips: '',
  minSpend: '',
  upcoming: '',
};

/**
 * The common questions, as starting points the operator can then edit. Each
 * REPLACES the booking inputs rather than merging, so picking "Lapsed" after
 * "Regulars" does not leave a minimum trip count behind.
 */
export const BOOKING_PRESETS: { key: string; label: string; hint: string; state: BookingFilterState }[] = [
  {
    key: 'recent',
    label: 'Booked recently',
    hint: 'A trip in the last 90 days',
    state: { ...EMPTY_BOOKINGS, lastTripWithinDays: '90' },
  },
  {
    key: 'lapsed',
    label: 'Win back',
    hint: 'Rode before, nothing in 90 days, nothing booked',
    state: { ...EMPTY_BOOKINGS, noTripForDays: '90', upcoming: 'exclude' },
  },
  {
    key: 'first',
    label: 'First-timers',
    hint: 'Exactly one trip so far',
    state: { ...EMPTY_BOOKINGS, minTrips: '1', maxTrips: '1' },
  },
  {
    key: 'regulars',
    label: 'Regulars',
    hint: '5 or more trips',
    state: { ...EMPTY_BOOKINGS, minTrips: '5' },
  },
  {
    key: 'never',
    label: 'Never booked',
    hint: 'On file, no trips yet',
    state: { ...EMPTY_BOOKINGS, maxTrips: '0' },
  },
];

/** The wire shape of the inputs, or undefined when none is set. */
export function toBookingFilters(state: BookingFilterState): CampaignBookingFilters | undefined {
  const out: CampaignBookingFilters = {};
  const whole = (value: string) => {
    const n = Number(value.trim());
    return value.trim() !== '' && Number.isInteger(n) && n >= 0 ? n : undefined;
  };
  const within = whole(state.lastTripWithinDays);
  if (within) out.lastTripWithinDays = within;
  const lapsed = whole(state.noTripForDays);
  if (lapsed) out.noTripForDays = lapsed;
  const min = whole(state.minTrips);
  if (min !== undefined) out.minTrips = min;
  const max = whole(state.maxTrips);
  if (max !== undefined) out.maxTrips = max;
  const spend = Number(state.minSpend.trim());
  if (state.minSpend.trim() !== '' && Number.isFinite(spend) && spend >= 0) out.minSpend = spend;
  if (state.upcoming) out.upcoming = state.upcoming;
  return Object.keys(out).length ? out : undefined;
}

/** A stored filter back into inputs — the inverse of toBookingFilters. */
export function fromBookingFilters(filters: CampaignBookingFilters | undefined): BookingFilterState {
  const text = (value: number | undefined) => (value === undefined ? '' : String(value));
  return {
    lastTripWithinDays: text(filters?.lastTripWithinDays),
    noTripForDays: text(filters?.noTripForDays),
    minTrips: text(filters?.minTrips),
    maxTrips: text(filters?.maxTrips),
    minSpend: text(filters?.minSpend),
    upcoming: filters?.upcoming ?? '',
  };
}

/** Booking history in words, for the summary line — null when not filtered on. */
export function describeBookings(
  filters: CampaignBookingFilters | undefined,
  // The tenant's own currency sign — never a bare "$" (see lib/tenantCurrency).
  money: (amount: number) => string = (amount) => formatTenantMoney(amount, 0),
): string | null {
  if (!filters) return null;
  const parts: string[] = [];
  const { lastTripWithinDays, noTripForDays, minTrips, maxTrips, minSpend, upcoming } = filters;
  if (maxTrips === 0) parts.push('never booked');
  else if (minTrips !== undefined && maxTrips !== undefined && minTrips === maxTrips) {
    parts.push(`exactly ${minTrips} trip${minTrips === 1 ? '' : 's'}`);
  } else {
    if (minTrips !== undefined && minTrips > 0) parts.push(`${minTrips}+ trips`);
    if (maxTrips !== undefined) parts.push(`at most ${maxTrips} trip${maxTrips === 1 ? '' : 's'}`);
  }
  if (lastTripWithinDays) parts.push(`rode in the last ${lastTripWithinDays} days`);
  if (noTripForDays) parts.push(`no trip in ${noTripForDays} days`);
  if (minSpend !== undefined) parts.push(`spent ${money(minSpend)}+`);
  if (upcoming === 'only') parts.push('has a trip booked');
  if (upcoming === 'exclude') parts.push('nothing booked');
  return parts.length ? parts.join(', ') : null;
}

export interface AudienceFilterState {
  statuses: AudienceStatus[];
  priorities: number[];
  vehicleTypes: number[];
  address: Record<AudienceAddressField, string>;
  bookings: BookingFilterState;
  mode: 'all' | 'manual';
  selected: string[];
  excluded: string[];
}

export function emptyFilterState(defaultStatuses: AudienceStatus[] = []): AudienceFilterState {
  return {
    statuses: [...defaultStatuses],
    priorities: [],
    vehicleTypes: [],
    address: { city: '', state: '', country: '', postalCode: '' },
    bookings: { ...EMPTY_BOOKINGS },
    mode: 'all',
    selected: [],
    excluded: [],
  };
}

/** The pool-defining filters (no individual picks) — what the member picker lists. */
export function toPoolFilters(state: AudienceFilterState): CampaignAudienceFilters | undefined {
  const filters: CampaignAudienceFilters = {};
  if (state.statuses.length) filters.statuses = state.statuses;
  if (state.priorities.length) filters.priorities = state.priorities;
  if (state.vehicleTypes.length) filters.vehicleTypes = state.vehicleTypes;
  const address: CampaignAudienceFilters['address'] = {};
  for (const { field } of ADDRESS_FIELDS) {
    const value = state.address[field].trim();
    if (value) address[field] = value;
  }
  if (Object.keys(address).length) filters.address = address;
  const bookings = toBookingFilters(state.bookings);
  if (bookings) filters.bookings = bookings;
  return Object.keys(filters).length ? filters : undefined;
}

/**
 * The wire filters for a state — undefined when it asks for nothing.
 *
 * The picks go into the pair that matches the CHANNEL. They are separate fields
 * on the wire rather than one renamed, because every existing campaign already
 * has `selectedEmails` stored and because a list validated as addresses cannot
 * also validate as E.164 numbers. Builder state keeps one list either way — a
 * campaign's channel never changes, so there is never more than one kind of
 * pick in flight.
 */
export function toApiFilters(
  state: AudienceFilterState,
  channel: CampaignChannel = 'email',
): CampaignAudienceFilters | undefined {
  const filters: CampaignAudienceFilters = { ...(toPoolFilters(state) ?? {}) };
  const sms = channel === 'sms';
  if (state.mode === 'manual') {
    if (state.selected.length) {
      if (sms) filters.selectedPhones = state.selected;
      else filters.selectedEmails = state.selected;
    }
  } else if (state.excluded.length) {
    if (sms) filters.excludedPhones = state.excluded;
    else filters.excludedEmails = state.excluded;
  }
  return Object.keys(filters).length ? filters : undefined;
}

/**
 * A stored draft's filters back into builder state — the inverse of
 * toApiFilters, so reopening a draft shows the targeting it was saved with.
 *
 * Statuses are taken literally rather than falling back to the audience's
 * defaults: a draft narrowed to 'pending' must not silently widen on reopen.
 * A draft that stored no filters at all never reaches here — it gets a fresh
 * emptyFilterState, defaults and all.
 */
export function fromApiFilters(filters: CampaignAudienceFilters): AudienceFilterState {
  const state = emptyFilterState();
  state.statuses = filters.statuses ? [...filters.statuses] : [];
  state.priorities = filters.priorities ? [...filters.priorities] : [];
  state.vehicleTypes = filters.vehicleTypes ? [...filters.vehicleTypes] : [];
  for (const { field } of ADDRESS_FIELDS) {
    state.address[field] = filters.address?.[field] ?? '';
  }
  state.bookings = fromBookingFilters(filters.bookings);
  // The server stores one or the other, never both — and the pair it stored
  // them in is decided by the campaign's channel, which is fixed. Reading both
  // pairs here means a reopened draft shows its picks whichever it is, without
  // this function having to be told.
  const selected = filters.selectedPhones ?? filters.selectedEmails;
  const excluded = filters.excludedPhones ?? filters.excludedEmails;
  if (selected?.length) {
    state.mode = 'manual';
    state.selected = [...selected];
  } else if (excluded?.length) {
    state.excluded = [...excluded];
  }
  return state;
}

/**
 * One-line targeting summary for the sidebar/review, or null when untargeted.
 * `vehicleTypeOptions` resolves ids to names — it's tenant data from the live
 * audience meta, not a static map like PRIORITY_LABELS, so the caller supplies
 * whatever it already fetched rather than this needing its own copy.
 */
export function describeFilters(
  state: AudienceFilterState | null,
  vehicleTypeOptions: { value: number; label: string }[] = [],
): string | null {
  if (!state) return null;
  if (state.mode === 'manual') {
    return `${state.selected.length.toLocaleString()} hand-picked`;
  }
  const parts: string[] = [];
  if (state.statuses.length) parts.push(state.statuses.join(' / '));
  if (state.priorities.length) {
    parts.push(state.priorities.map((value) => PRIORITY_LABELS[value] ?? String(value)).join(' / '));
  }
  if (state.vehicleTypes.length) {
    const labelOf = new Map(vehicleTypeOptions.map(({ value, label }) => [value, label]));
    parts.push(state.vehicleTypes.map((value) => labelOf.get(value) ?? String(value)).join(' / '));
  }
  const place = ADDRESS_FIELDS.map(({ field }) => state.address[field].trim()).filter(Boolean);
  if (place.length) parts.push(place.join(', '));
  const bookings = describeBookings(toBookingFilters(state.bookings ?? EMPTY_BOOKINGS));
  if (bookings) parts.push(bookings);
  if (state.excluded.length) parts.push(`${state.excluded.length} excluded`);
  return parts.length ? parts.join(' · ') : null;
}

export interface CustomRow {
  email?: string;
  phoneNumber?: string;
  name?: string;
}

/**
 * The console's copy of the server's `normalizePhone` (utils/phone.js), and it
 * must agree with it: a pasted list is validated here to give the operator a
 * count before they press anything, and validated again on the way in — so a
 * number this accepts and the server refuses is a campaign that saves and then
 * will not send.
 *
 * The bare-ten-digit guess is +1, matching the service, because that is how
 * dispatch stores US numbers. Anything else without a country code is refused
 * rather than guessed: a London number adopted as a US one is a recipient no
 * text can ever reach.
 */
export function normalizePhone(value: string): string | null {
  const digits = value.replace(/[^\d+]/g, '');
  if (/^\+[1-9]\d{9,14}$/.test(digits)) return digits;
  if (/^\d{10}$/.test(digits)) return `+1${digits}`;
  if (/^1\d{10}$/.test(digits)) return `+${digits}`;
  return null;
}

/**
 * One recipient per line: `a@b.com, Ada` / `a@b.com;Ada` / a bare address — or,
 * on a text campaign, `+15165550100, Ada` and the same separators.
 *
 * The name is whatever is left on the line after the address, which is why the
 * address has to be FOUND rather than assumed first: operators paste columns in
 * whatever order their spreadsheet had them.
 */
export function parseCustomList(text: string, channel: CampaignChannel = 'email'): CustomRow[] {
  const rows: CustomRow[] = [];
  const seen = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const parts = line.split(/[,;\t]/).map((part) => part.trim().replace(/^"|"$/g, ''));
    if (channel === 'sms') {
      // A part that normalizes is the number; anything shorter is a name that
      // happens to contain digits, and `normalizePhone` refuses it.
      const raw = parts.find((part) => part && normalizePhone(part) !== null);
      const phoneNumber = raw ? normalizePhone(raw) : null;
      if (!phoneNumber || seen.has(phoneNumber)) continue;
      seen.add(phoneNumber);
      const name = parts.filter((part) => part && part !== raw).join(' ') || undefined;
      rows.push(name ? { phoneNumber, name } : { phoneNumber });
      continue;
    }
    const email = parts.find((part) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(part));
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    const name = parts.filter((part) => part && part !== email).join(' ') || undefined;
    rows.push(name ? { email, name } : { email });
  }
  return rows;
}

/** Same tag grammar the server substitutes with — padding inside the braces included. */
const TAG_RE = /\{\{\s*([a-z][a-z0-9_]*)\s*\}\}/g;

/**
 * Substitute merge tags for the live preview: a sample recipient, plus this
 * tenant's real data points from `GET /campaigns/merge_fields`.
 *
 * Mirrors the server's `utils/mergeFields.js` — an unknown tag is left as
 * written, so a typo shows up here instead of mailing a blank. A tenant tag with
 * no value yet (the fetch is still in flight) is left alone for the same reason.
 */
export function previewMerge(
  text: string,
  sample?: CustomRow,
  tenantValues: Record<string, string> = {},
): string {
  const values: Record<string, string> = {
    ...tenantValues,
    name: sample?.name || 'Rhea Kapoor',
    email: sample?.email || 'rhea@example.com',
    phone: sample?.phoneNumber || '+15165550100',
    address: sample?.phoneNumber || sample?.email || 'rhea@example.com',
  };
  return text.replaceAll(TAG_RE, (match, key: string) => values[key] ?? match);
}

/**
 * The tags the send path has always substituted from the recipient row.
 *
 * They need no server round-trip, so the builder lists them even when
 * `GET /campaigns/merge_fields` is missing or fails — a backend that predates
 * that endpoint then loses the company data points, not the whole panel.
 */
export const RECIPIENT_MERGE_FIELDS: MergeField[] = [
  {
    key: 'name',
    tag: '{{name}}',
    label: 'Recipient name',
    group: 'recipient',
    hint: 'Falls back to "there" when dispatch has no name',
    value: null,
  },
  { key: 'email', tag: '{{email}}', label: 'Recipient email', group: 'recipient', hint: null, value: null },
];

/** The server's vocabulary over the baseline — its own copy of a tag wins. */
export function withBaselineFields(fields: MergeField[]): MergeField[] {
  const served = new Set(fields.map((field) => field.key));
  return [...RECIPIENT_MERGE_FIELDS.filter((field) => !served.has(field.key)), ...fields];
}

/** The `{{tag}} → value` map the preview needs, from the API's field list. */
export function mergeValuesOf(fields: MergeField[]): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of fields) {
    if (field.value) values[field.key] = field.value;
  }
  return values;
}

/**
 * The delivery choice shown on the review step.
 *
 * Returns null when there is nothing to ask: the audience fits in one day of the
 * mailbox's allowance, so sending is unremarkable and a picker would be noise.
 *
 * The mailbox's actual cap is never surfaced — the API does not return it to
 * tenants, and putting a number on screen invites arguing with it.
 */
export function deliveryChoice(options: SendingOptions | SmsSendingOptions | null): {
  spreadDetail: string;
  sesDetail: string;
  sesEnabled: boolean;
} | null {
  // A text campaign has one transport — the tenant's own number, on their own
  // carrier bill — so there is no choice to offer and no credits to weigh.
  if (!options || isSmsOptions(options) || options.fitsToday) return null;
  return {
    spreadDetail:
      `Finishes in about ${options.projectedDays} days. Replies keep working ` +
      'the whole time. No credits used.',
    // One message for both "not enough credits" and "SES not configured": the
    // server does not distinguish them to the tenant, and neither should this.
    sesDetail: options.canSendViaSes
      ? `Delivers in one run. Uses ${options.creditsRequired.toLocaleString()} of your ` +
        `${options.creditBalance.toLocaleString()} credits.`
      : `Needs ${options.creditsRequired.toLocaleString()} credits — you have ` +
        `${options.creditBalance.toLocaleString()}. Contact your account manager to top up.`,
    sesEnabled: options.canSendViaSes,
  };
}

/** The Summary sidebar's "Send" line, so the choice shows up in the review too. */
export function sendModeSummary(
  options: SendingOptions | SmsSendingOptions | null,
  mode: CampaignSendMode,
): string | null {
  if (!options || isSmsOptions(options) || options.fitsToday) return null;
  return mode === 'spread'
    ? `Over ${options.projectedDays} days`
    : `Now · ${options.creditsRequired.toLocaleString()} credits`;
}

/**
 * The mode a send should actually use.
 *
 * Falls back to 'spread' whenever no choice is on offer, so a credit selection
 * made for a larger audience can never quietly follow the user to a list that
 * fits for free.
 */
export function effectiveSendMode(
  options: SendingOptions | SmsSendingOptions | null,
  chosen: CampaignSendMode,
): CampaignSendMode {
  const choice = deliveryChoice(options);
  if (!choice) return 'spread';
  // Also covers the balance falling short after the choice was made: relying on
  // an effect to reset the stored mode leaves a window where the send would be
  // submitted in a mode the server rejects.
  if (chosen === 'ses' && !choice.sesEnabled) return 'spread';
  return chosen;
}

/**
 * The unsubscribe footer's default wording, mirroring `utils/unsubscribe.js`.
 *
 * Shown as the INPUT PLACEHOLDER rather than as a value, so leaving the box
 * empty and seeing the default are the same picture — which is the honest one,
 * because empty is exactly what sends the default. Duplicated across the wire
 * like `smsSegments`, and for the same reason: the operator has to see what
 * will go out before they save, and a round trip per keystroke is not that.
 */
export const DEFAULT_LINK_TEXT = 'Unsubscribe from these emails';
export const defaultFooterText = (companyName?: string | null) =>
  `You are receiving this because you have a relationship with ${companyName || 'us'}.`;

/** The cap choices — a day to a month; the server refuses anything past 720 hours. */
export const FREQUENCY_CAPS = [
  { hours: 24, label: 'day' },
  { hours: 48, label: '2 days' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: 'week' },
  { hours: 336, label: '2 weeks' },
];

/** Minutes after midnight as the `HH:MM` a time input holds. */
export function minutesToClock(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** `HH:MM` back to minutes, or null for a cleared input. */
export function clockToMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return minutes >= 0 && minutes < 24 * 60 ? minutes : null;
}
